import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Stack, Tooltip } from '@mui/material';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import LaunchIcon from '@mui/icons-material/Launch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import GhostButton from '../../shared/button/GhostButton';
import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import {
  deleteFile,
  fetchAdminFiles,
  restoreFile,
  retryFile,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { AdminFileItem, ProblemDetail } from '../../../types/syntiro_honey';
import type { ReviewFileSeed } from '../../../types/syntiro_honey';
import { formatSkippedSheets } from '../review/value_format';
import FileAuditDialog from './FileAuditDialog';

/** File-status chip colour; anything non-terminal reads as in-progress info. */
const FILE_STATUS_COLOR: Record<string, 'success' | 'error' | 'info'> = {
  PROCESSED: 'success',
  FAILED: 'error'
};

interface AdminPanelProps {
  /** Jump to the Review tab pre-filtered to one file. */
  onReviewFile: (seed: ReviewFileSeed) => void;
}

/**
 * Phase-6 admin/management screen: every file (all statuses, INCLUDING
 * soft-deleted — they must be visible to be restorable) with record counts and
 * the quality summary, plus the per-file actions: open in review, view the
 * attributed audit trail, soft-delete (cascades to records) and restore
 * (re-opens exactly the cascaded cohort). Admin-gated by the container.
 * @returns {JSX.Element}
 */
export default function AdminPanel({ onReviewFile }: AdminPanelProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [rows, setRows] = useState<Array<AdminFileItem>>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  const [auditFile, setAuditFile] = useState<AdminFileItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminFileItem | null>(null);
  const [pendingRetry, setPendingRetry] = useState<AdminFileItem | null>(null);
  const [actionBusy, setActionBusy] = useState<boolean>(false);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem(null);
    try {
      const response = await fetchAdminFiles(
        paginationModel.pageSize,
        paginationModel.page * paginationModel.pageSize
      );
      setRows(response.items);
      setTotal(response.total);
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setLoading(false);
    }
  }, [paginationModel]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (): Promise<void> => {
    if (pendingDelete === null) {
      return;
    }
    setActionBusy(true);
    try {
      await deleteFile(pendingDelete.id);
      setFeedbackData({
        state: true,
        type: 'success',
        message: `"${pendingDelete.filename}" deleted with its ${pendingDelete.recordCount} record${pendingDelete.recordCount === 1 ? '' : 's'} — reversible via Restore.`
      });
      await load();
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setActionBusy(false);
      setPendingDelete(null);
    }
  };

  const handleRetry = async (): Promise<void> => {
    if (pendingRetry === null) {
      return;
    }
    setActionBusy(true);
    try {
      await retryFile(pendingRetry.id);
      setFeedbackData({
        state: true,
        type: 'success',
        message: `"${pendingRetry.filename}" is re-processing — its previous records are superseded and the new extraction will appear in Review.`
      });
      await load();
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setActionBusy(false);
      setPendingRetry(null);
    }
  };

  const handleRestore = useCallback(
    async (file: AdminFileItem): Promise<void> => {
      setActionBusy(true);
      try {
        await restoreFile(file.id);
        setFeedbackData({
          state: true,
          type: 'success',
          message: `"${file.filename}" restored, including the records its deletion removed.`
        });
        await load();
      } catch (error: unknown) {
        setProblem(toProblemDetail(error));
      } finally {
        setActionBusy(false);
      }
    },
    [load, setFeedbackData]
  );

  // Memoised so the DataGrid keeps one column identity across the component's many
  // state transitions. actionBusy MUST stay in the deps: the action renderCells
  // read it, and a stale closure would leave the buttons permanently disabled
  // after the first action.
  const columns: Array<GridColDef<AdminFileItem>> = useMemo(
    () => [
      {
        field: 'filename',
        headerName: 'File',
        flex: 1.4,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<AdminFileItem>) => (
          <Box sx={{ lineHeight: 1.3, overflow: 'hidden' }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <BodyText
                noWrap
                sx={params.row.deletedAt !== null ? { textDecoration: 'line-through' } : {}}
              >
                {params.row.filename}
              </BodyText>
              {(params.row.skippedSheets ?? []).length > 0 && (
                <Tooltip title={formatSkippedSheets(params.row.skippedSheets ?? [])}>
                  {/* titleAccess names the icon for assistive tech; tabIndex lets a
                    keyboard user focus it, which also opens the tooltip. */}
                  <WarningAmberIcon
                    color="warning"
                    sx={{ fontSize: 15 }}
                    tabIndex={0}
                    titleAccess={`Skipped sheets — ${formatSkippedSheets(
                      params.row.skippedSheets ?? []
                    )}`}
                  />
                </Tooltip>
              )}
            </Stack>
            <CaptionText noWrap>
              {/* The uid stays as the fallback so files uploaded before the API
                  resolved names remain attributed. */}
              {new Date(params.row.createdAt).toLocaleString()} ·{' '}
              {params.row.uploadedByName ?? params.row.uploadedBy ?? '—'}
            </CaptionText>
          </Box>
        )
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 130,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AdminFileItem>) =>
          params.row.deletedAt !== null ? (
            <Chip size="small" label="Deleted" color="default" variant="outlined" />
          ) : (
            <Chip
              size="small"
              label={params.row.status}
              color={FILE_STATUS_COLOR[params.row.status] ?? 'info'}
              variant="outlined"
            />
          )
      },
      {
        field: 'recordCount',
        headerName: 'Records',
        // The grid clips a cell, it never wraps it, so this has to hold the whole
        // "N mapped · N approved" caption; the small flex spends surplus width here
        // (headroom for four-digit counts) instead of on the filename column.
        minWidth: 200,
        flex: 0.6,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AdminFileItem>) => (
          <Box sx={{ lineHeight: 1.3 }}>
            <BodyText>{params.row.recordCount}</BodyText>
            <CaptionText>
              {params.row.mappedCount} mapped · {params.row.approvedCount} approved
            </CaptionText>
          </Box>
        )
      },
      {
        field: 'quality',
        headerName: 'Quality',
        minWidth: 190,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AdminFileItem>) => (
          // The Phase-6 acceptance line: "50 high / 10 medium / 5 low". The band
          // letter is IN the visible label (colour must not be the only carrier),
          // and role="img" makes each aria-label a real accessible name.
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="High confidence (≥90%)">
              <Chip
                size="small"
                color="success"
                variant="outlined"
                role="img"
                aria-label={`${params.row.highCount} high confidence (90% or more)`}
                label={`H ${params.row.highCount}`}
              />
            </Tooltip>
            <Tooltip title="Medium confidence (70–90%)">
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                role="img"
                aria-label={`${params.row.mediumCount} medium confidence (70 to 90%)`}
                label={`M ${params.row.mediumCount}`}
              />
            </Tooltip>
            <Tooltip title="Low confidence (<70%)">
              <Chip
                size="small"
                color="error"
                variant="outlined"
                role="img"
                aria-label={`${params.row.lowCount} low confidence (below 70%)`}
                label={`L ${params.row.lowCount}`}
              />
            </Tooltip>
          </Stack>
        )
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 320,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AdminFileItem>) => (
          <Stack direction="row" spacing={0.5}>
            <GhostButton
              dark
              disabled={params.row.deletedAt !== null || params.row.recordCount === 0}
              startIcon={<LaunchIcon />}
              onClick={() => onReviewFile({ fileId: params.row.id, filename: params.row.filename })}
            >
              Review
            </GhostButton>
            <GhostButton dark startIcon={<HistoryIcon />} onClick={() => setAuditFile(params.row)}>
              Audit
            </GhostButton>
            {params.row.deletedAt === null && (
              <GhostButton
                dark
                disabled={
                  actionBusy || params.row.status === 'PROCESSING' || params.row.status === 'QUEUED'
                }
                startIcon={<ReplayIcon />}
                onClick={() => setPendingRetry(params.row)}
              >
                Retry
              </GhostButton>
            )}
            {params.row.deletedAt === null ? (
              <GhostButton
                dark
                outlined
                disabled={actionBusy}
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setPendingDelete(params.row)}
              >
                Delete
              </GhostButton>
            ) : (
              <GhostButton
                dark
                outlined
                disabled={actionBusy}
                startIcon={<RestoreFromTrashIcon />}
                onClick={() => void handleRestore(params.row)}
              >
                Restore
              </GhostButton>
            )}
          </Stack>
        )
      }
    ],
    [actionBusy, onReviewFile, handleRestore]
  );

  return (
    <Box sx={{ py: 3 }}>
      <Stack spacing={2}>
        {problem !== null && (
          <Alert severity="error" onClose={() => setProblem(null)}>
            {problem.detail ?? problem.title ?? 'Loading the admin file list failed.'}
          </Alert>
        )}
        <DataGrid
          autoHeight
          // Small page sizes + few columns: virtualization buys nothing here and
          // disabling it lets tests (jsdom has no viewport) render every cell.
          disableVirtualization
          rows={rows}
          columns={columns}
          rowCount={total}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          getRowHeight={() => 56}
        />
      </Stack>
      <FileAuditDialog file={auditFile} onClose={() => setAuditFile(null)} />
      <ConfirmationModal
        open={pendingRetry !== null}
        title="Re-process this file?"
        description={`Run "${pendingRetry?.filename ?? ''}" through the extraction pipeline again. Its current ${pendingRetry?.recordCount ?? 0} record${(pendingRetry?.recordCount ?? 0) === 1 ? '' : 's'} (including any review work on them) will be superseded by the new extraction.`}
        confirm_button_text="Re-process"
        confirm_loading={actionBusy}
        handleCloseModal={() => setPendingRetry(null)}
        handleConfirm={handleRetry}
      />
      <ConfirmationModal
        open={pendingDelete !== null}
        title="Delete this file and its records?"
        description={`Soft-delete "${pendingDelete?.filename ?? ''}" and its ${pendingDelete?.recordCount ?? 0} record${(pendingDelete?.recordCount ?? 0) === 1 ? '' : 's'}. This is reversible: the file stays listed and Restore brings back exactly the records this deletion removes.`}
        confirm_button_text="Delete"
        confirm_loading={actionBusy}
        handleCloseModal={() => setPendingDelete(null)}
        handleConfirm={handleDelete}
      />
    </Box>
  );
}
